import { z } from 'zod';

// src/singleton/index.ts
var NAMESPACE = "@authe/fe-commons/singleton/";
function globalSingleton(key, init) {
  const slot = /* @__PURE__ */ Symbol.for(NAMESPACE + key);
  const realm = globalThis;
  const existing = realm[slot];
  if (existing !== void 0) return existing;
  const created = init();
  realm[slot] = created;
  return created;
}
var BRAND_NAMESPACE = "@authe/fe-commons/brand/";
var ordinaryHasInstance = Function.prototype[Symbol.hasInstance];
function brandClass(cls, key) {
  const brand = /* @__PURE__ */ Symbol.for(BRAND_NAMESPACE + key);
  Object.defineProperty(cls.prototype, brand, { value: true });
  Object.defineProperty(cls, Symbol.hasInstance, {
    value(value) {
      if (this !== cls) return ordinaryHasInstance.call(this, value);
      return typeof value === "object" && value !== null && value[brand] === true;
    }
  });
}

// src/display/truncation.ts
var ELLIPSIS = "\u2026";
var DEFAULT_TRUNCATION = {
  hash: { head: 10, tail: 8 },
  address: { head: 6, tail: 4 }
};
var truncation = globalSingleton("display/truncation", () => ({
  active: DEFAULT_TRUNCATION
}));
function configureTruncation(partial) {
  const current = truncation.active;
  truncation.active = {
    hash: { ...current.hash, ...partial.hash },
    address: { ...current.address, ...partial.address }
  };
}
function getTruncation(kind) {
  const { active } = truncation;
  return kind === "hash" ? active.hash : active.address;
}
function hexPrefixOf(value) {
  return /^0x/i.test(value) ? value.slice(0, 2) : "";
}
function truncateMiddle(value, lengths) {
  const text2 = value.trim();
  if (/\s/.test(text2)) return value;
  const prefix = hexPrefixOf(text2);
  const body = text2.slice(prefix.length);
  if (body.length <= lengths.head + lengths.tail + ELLIPSIS.length) return text2;
  return `${prefix}${body.slice(0, lengths.head)}${ELLIPSIS}${body.slice(-lengths.tail)}`;
}
function resetTruncationConfig() {
  truncation.active = DEFAULT_TRUNCATION;
}
var LONG_HEX_RUN = /0x[0-9a-fA-F]{24,}/g;
function shortenIdentifiersIn(text2) {
  return text2.replace(LONG_HEX_RUN, (match) => {
    const kind = match.length > 42 ? "hash" : "address";
    return truncateMiddle(match, getTruncation(kind));
  });
}
z.object({ "kind": z.any().default("abi"), "id": z.union([z.string(), z.null()]).default(null), "external_id": z.union([z.string(), z.null()]).default(null), "url": z.union([z.string(), z.null()]).default(null), "raw_payload_ref": z.union([z.string(), z.null()]).default(null), "visibility": z.union([z.any(), z.null()]).default(null) }).describe('Who / how asserted an :class:`Evidence` entry.\n\n``id`` is the source-local identifier: a provider slug (``"moralis"``), an\nABI source (``"sourcify"``), a protocol-plugin id, or a user id \u2014 depending\non :attr:`kind`. For ``kind == PROVIDER`` the id is normalised to a\ncanonical provider slug via :func:`commons.providers.normalize_provider_id`\n(mirrors the old ``ProviderAttribution.provider_id`` discipline).');
var EvidenceSchema = z.object({ "source": z.any().optional(), "confidence": z.number().gte(0).lte(1).default(1), "reasons": z.array(z.string()).optional(), "signals": z.record(z.string(), z.any()).optional(), "asserted_fields": z.array(z.string()).optional(), "asserted_at": z.union([z.number().int(), z.null()]).default(null), "last_seen_at": z.union([z.number().int(), z.null()]).default(null), "context": z.record(z.string(), z.any()).optional(), "metadata": z.record(z.string(), z.any()).optional() }).describe("One assertion: *this source claims X, with this confidence, because \u2026*.\n\nReplaces both ``SemanticEvidence`` (decode) and ``ProviderAttribution``\n(catalog). ``confidence`` is clamped to ``[0, 1]``. ``asserted_at`` /\n``last_seen_at`` default to wall-clock seconds so a fresh assertion merges\nwith an older one without a ``None`` branch (see\n:func:`commons.evidence.engine.merge_evidence`).\n\nDecode-specific coordinates (chain / block / cross-chain hop / proxy\ntraversal) live in :attr:`context` under the ``CTX_*`` keys; read them via\nthe convenience properties (:attr:`chain_id`, :attr:`dest_chain_id`, \u2026).");

// src/models/wire/graph-scene.ts
var SCENE_DETAIL_TIERS = ["compact", "standard", "expanded"];
var MediaRefSchema = z.object({
  kind: z.string().default("placeholder"),
  value: z.string(),
  fallback: z.union([z.string(), z.null()]).default(null),
  alt: z.union([z.string(), z.null()]).default(null)
});
var SceneAssetSchema = z.object({
  token_address: z.union([z.string(), z.null()]).default(null),
  token_chain_id: z.union([z.string(), z.null()]).default(null),
  symbol: z.union([z.string(), z.null()]).default(null),
  decimals: z.union([z.number().int(), z.null()]).default(null),
  amount_raw: z.union([z.string(), z.null()]).default(null),
  amount: z.union([z.string(), z.null()]).default(null),
  usd: z.union([z.string(), z.null()]).default(null),
  direction: z.union([z.string(), z.null()]).default(null),
  // 'actual' | 'quoted_minimum' — mirrors commons AmountBasis. Lets a label
  // flag a swap min-return floor as "(min)" instead of an exact amount.
  basis: z.union([z.string(), z.null()]).default(null),
  min_amount_raw: z.union([z.string(), z.null()]).default(null),
  min_amount: z.union([z.string(), z.null()]).default(null),
  icon: z.union([MediaRefSchema, z.null()]).default(null)
});
var SceneSourceRefSchema = z.object({
  ref_type: z.string(),
  address: z.union([z.string(), z.null()]).default(null),
  log_index: z.union([z.number().int(), z.null()]).default(null),
  internal_tx_index: z.union([z.number().int(), z.null()]).default(null),
  path: z.array(z.string()).default([])
});
var SceneFactSchema = z.object({
  key: z.string(),
  label: z.string().default(""),
  kind: z.union([z.string(), z.null()]).default(null),
  value: z.unknown(),
  protocol_id: z.union([z.string(), z.null()]).default(null),
  source: z.union([z.string(), z.null()]).default(null),
  path_id: z.union([z.string(), z.null()]).default(null),
  semantic_node_id: z.union([z.string(), z.null()]).default(null),
  source_refs: z.array(SceneSourceRefSchema).default([]),
  evidence: z.array(EvidenceSchema).default([]),
  confidence: z.union([z.number().min(0).max(1), z.null()]).default(null),
  icon: z.union([z.string(), z.null()]).default(null),
  description: z.union([z.string(), z.null()]).default(null)
});
var SceneNodeSchema = z.object({
  id: z.string(),
  kind: z.string().default("address"),
  label: z.string().default(""),
  sublabel: z.union([z.string(), z.null()]).default(null),
  chain_id: z.union([z.string(), z.null()]).default(null),
  address: z.union([z.string(), z.null()]).default(null),
  group_id: z.union([z.string(), z.null()]).default(null),
  off_chain: z.boolean().default(false),
  entity_id: z.union([z.string(), z.null()]).default(null),
  icon: z.union([MediaRefSchema, z.null()]).default(null),
  assets: z.array(SceneAssetSchema).default([]),
  value: z.union([z.string(), z.null()]).default(null),
  badge: z.union([z.string(), z.null()]).default(null),
  attributes: z.record(z.string(), z.unknown()).default({}),
  source_refs: z.array(SceneSourceRefSchema).default([]),
  evidence: z.array(EvidenceSchema).default([]),
  confidence: z.number().min(0).max(1).default(1),
  detail_tier: z.string().default("compact"),
  facts: z.array(SceneFactSchema).default([]),
  origin: z.string().default("decoded")
});
var SceneEdgeSchema = z.object({
  id: z.string(),
  source: z.string(),
  target: z.string(),
  kind: z.string().default("flow"),
  label: z.union([z.string(), z.null()]).default(null),
  asset: z.union([SceneAssetSchema, z.null()]).default(null),
  direction: z.union([z.string(), z.null()]).default(null),
  from_chain: z.union([z.string(), z.null()]).default(null),
  to_chain: z.union([z.string(), z.null()]).default(null),
  cross_chain: z.boolean().default(false),
  virtual: z.boolean().default(false),
  icon: z.union([MediaRefSchema, z.null()]).default(null),
  attributes: z.record(z.string(), z.unknown()).default({}),
  detail_tier: z.string().default("compact"),
  facts: z.array(SceneFactSchema).default([]),
  origin: z.string().default("decoded")
});
var SceneGroupSchema = z.object({
  id: z.string(),
  group_type: z.string().default("chain"),
  chain_id: z.union([z.string(), z.null()]).default(null),
  label: z.string().default(""),
  member_node_ids: z.array(z.string()).default([]),
  color: z.union([z.string(), z.null()]).default(null),
  icon: z.union([MediaRefSchema, z.null()]).default(null)
});
z.object({
  version: z.string().default("1"),
  kind: z.string().default("semantic"),
  summary: z.union([z.string(), z.null()]).default(null),
  nodes: z.array(SceneNodeSchema).default([]),
  edges: z.array(SceneEdgeSchema).default([]),
  groups: z.array(SceneGroupSchema).default([]),
  // The transaction's headline "business metrics" — the SAME resolved
  // `key_attributes` groups the detail-page highlights panel shows, carried on
  // EVERY chart kind so the graph view renders a consistent highlights strip.
  // Kept as the raw snake_case wire shape (a passthrough) so the wire layer
  // stays below `storage`; a consumer normalises it with `parseKeyAttributeGroups`
  // (the same parser the detail panel + group view use) → `KeyAttributeGroup[]`.
  highlights: z.array(z.record(z.string(), z.unknown())).default([]),
  chain_ids: z.array(z.string()).default([]),
  multi_chain: z.boolean().default(false),
  layout_hint: z.string().default("hierarchical"),
  // Compact-first: mirrors the backend `GraphBuildPolicy.default_view_tier`
  // default so a scene missing the field opens on the minimal intent + highlights
  // (the user raises the tier live). The scene always carries every tier.
  default_detail_tier: z.string().default("compact"),
  attributes: z.record(z.string(), z.unknown()).default({})
});
z.object({
  nodes: z.array(SceneNodeSchema).default([]),
  edges: z.array(SceneEdgeSchema).default([]),
  groups: z.array(SceneGroupSchema).default([]),
  hidden_node_ids: z.array(z.string()).default([]),
  hidden_edge_ids: z.array(z.string()).default([]),
  node_patches: z.record(z.string(), z.record(z.string(), z.unknown())).default({}),
  edge_patches: z.record(z.string(), z.record(z.string(), z.unknown())).default({}),
  /**
   * Where the author put an authored node. Honoured as a pin, not a hint, and
   * only for nodes the overlay itself added — pinning a decoded node freezes a
   * layout the backend is entitled to change, and the position was never the
   * author's to give.
   */
  positions: z.record(z.string(), z.object({ x: z.number(), y: z.number() })).default({}),
  /**
   * Who asserted this overlay, when, and why. Stamped by the service from the
   * caller's identity, never read from the element bodies — a client can no more
   * forge an author than it can forge `origin`.
   */
  authored_by: z.union([z.string(), z.null()]).default(null),
  authored_at: z.union([z.string(), z.null()]).default(null),
  authored_reason: z.union([z.string(), z.null()]).default(null),
  /**
   * The tier this overlay was published at, stamped by the read.
   *
   * On the BODY rather than left on the row, because the body is all a fold
   * receives — and without it a fold cannot tell one overlay overriding a lower
   * rung (precedence working) from two peers at the same rung contradicting each
   * other. Written by `overlay_wire` on the API and by the tx_graph store.
   */
  authored_scope: z.union([z.string(), z.null()]).default(null)
});
var GROUPING_DIMENSIONS = [
  "hop",
  "chain",
  "protocol",
  "entity",
  "tag",
  "label",
  "kind",
  "saga"
];
var GroupingDimensionOptionsSchema = z.object({
  /** Drop a band with fewer members than this (a band of one is just a node). */
  min_members: z.number().int().positive().optional(),
  /** Keep at most this many bands (largest first); the rest go ungrouped. */
  max_bands: z.number().int().positive().optional()
});
z.object({
  dimensions: z.array(z.enum(GROUPING_DIMENSIONS)).default([]),
  options: z.record(z.string(), GroupingDimensionOptionsSchema).default({})
});
function parseGroupingSpec(value) {
  if (!value || typeof value !== "object") return null;
  const raw = value;
  if (!Array.isArray(raw.dimensions)) return null;
  const known = new Set(GROUPING_DIMENSIONS);
  const seen = /* @__PURE__ */ new Set();
  const dimensions = [];
  for (const entry of raw.dimensions) {
    if (typeof entry !== "string" || !known.has(entry) || seen.has(entry)) continue;
    seen.add(entry);
    dimensions.push(entry);
  }
  const options = {};
  if (raw.options && typeof raw.options === "object") {
    for (const [key, opts] of Object.entries(raw.options)) {
      if (!known.has(key)) continue;
      const parsed = GroupingDimensionOptionsSchema.safeParse(opts);
      if (parsed.success) options[key] = parsed.data;
    }
  }
  return Object.keys(options).length > 0 ? { dimensions, options } : { dimensions };
}
var COMPACT_BALANCE = new Intl.NumberFormat("en-US", {
  notation: "compact",
  maximumFractionDigits: 2
});
function formatTokenBalance(value) {
  if (value == null || !Number.isFinite(value)) return "\u2014";
  const abs = Math.abs(value);
  if (abs === 0) return "0";
  if (abs >= 1e6) return COMPACT_BALANCE.format(value);
  if (abs >= 1) {
    return value.toLocaleString("en-US", { maximumFractionDigits: 4 });
  }
  if (abs < 1e-4) return value < 0 ? ">-0.0001" : "<0.0001";
  return value.toLocaleString("en-US", { maximumFractionDigits: 6 });
}
var SUB_PRECISION_SIGNIFICANT_DIGITS = 2;
var SUB_PRECISION_MAX_FRACTION = 12;
function subPrecisionAmount(whole, fraction, maxFrac) {
  const firstSignificant = fraction.search(/[1-9]/);
  const keep = firstSignificant + SUB_PRECISION_SIGNIFICANT_DIGITS;
  if (firstSignificant >= 0 && keep <= SUB_PRECISION_MAX_FRACTION) {
    const kept = fraction.slice(0, keep).replace(/0+$/, "");
    return `${whole}.${kept}`;
  }
  if (!isZeroText(whole)) return whole;
  const bound = `0.${"0".repeat(Math.max(maxFrac - 1, 0))}1`;
  return whole.startsWith("-") ? `>-${bound}` : `<${bound}`;
}
var UNLIMITED_AMOUNTS = [
  (1n << 256n) - 1n,
  (1n << 255n) - 1n,
  (1n << 96n) - 1n
];
var UNLIMITED_DIGITS = new Set(UNLIMITED_AMOUNTS.map((value) => value.toString()));
function isUnlimitedAmount(value) {
  if (value == null || typeof value === "number") return false;
  const digits = String(value).trim().replace(/[,\s_]/g, "").replace(/^[-+]/, "").replace(".", "").replace(/^0+(?=\d)/, "");
  return UNLIMITED_DIGITS.has(digits);
}
function isZeroText(value) {
  return value === "0" || value === "-0";
}
function formatGraphAmount(amount, opts = {}) {
  if (amount == null) return null;
  const exact = String(amount).trim();
  if (!exact) return null;
  const maxChars = opts.maxChars ?? 12;
  if (exact.length <= maxChars) {
    return { text: exact, title: exact, abbreviated: false };
  }
  const numeric = DECIMAL_RE.test(exact) ? Number(exact) : Number.NaN;
  if (Number.isFinite(numeric)) {
    if (Math.abs(numeric) >= 1e6) {
      const compact = formatTokenBalance(numeric);
      if (compact.length > maxChars) {
        return { text: numeric.toExponential(2), title: exact, abbreviated: true };
      }
      return { text: compact, title: exact, abbreviated: true };
    }
    const [whole = "", fraction = ""] = exact.split(".");
    const room = Math.max(2, maxChars - whole.length - 2);
    if (fraction.length > room) {
      const kept = fraction.slice(0, room).replace(/0+$/, "");
      if (!kept) {
        return { text: subPrecisionAmount(whole, fraction, room), title: exact, abbreviated: true };
      }
      return { text: `${whole}.${kept}\u2026`, title: exact, abbreviated: true };
    }
  }
  return { text: `${exact.slice(0, maxChars)}\u2026`, title: exact, abbreviated: true };
}
var DECIMAL_RE = /^-?\d+(?:\.\d+)?$/;
var LONG_NUMBER_RE = /(?<![0-9A-Za-z])\d[\d,]*(?:\.\d+)?(?![0-9A-Za-z])/g;
function shortenLongNumbers(text2, opts = {}) {
  const maxDigits = opts.maxDigits ?? 12;
  return text2.replace(LONG_NUMBER_RE, (match) => {
    const bare = match.replace(/,/g, "");
    if (isUnlimitedAmount(bare)) return "Unlimited";
    if (match.length <= maxDigits) return match;
    const display = formatGraphAmount(bare, { maxChars: maxDigits });
    return display ? display.text : match;
  });
}
var DECIMAL_TEXT = /^[+-]?(\d+(\.\d*)?|\.\d+)([eE][+-]?\d+)?$/;
function coerceNumber(value) {
  if (typeof value === "number") return Number.isFinite(value) ? value : void 0;
  if (typeof value !== "string") return void 0;
  const text2 = value.trim();
  if (text2 === "" || !DECIMAL_TEXT.test(text2)) return void 0;
  const parsed = Number(text2);
  return Number.isFinite(parsed) ? parsed : void 0;
}

// src/display/coerce.ts
function str(value) {
  return value == null || value === "" ? void 0 : String(value);
}
function asString(value) {
  return typeof value === "string" && value !== "" ? value : void 0;
}
function asTrimmedString(value) {
  const text2 = typeof value === "string" ? value.trim() : "";
  return text2 === "" ? void 0 : text2;
}
function text(value) {
  return value == null ? "" : String(value);
}
function num(value) {
  return coerceNumber(value);
}

// src/display/chainText.ts
var UNSAFE_CHARS = /[\p{Default_Ignorable_Code_Point}\u200B-\u200F\u061C\u202A-\u202E\u2060\u2066-\u2069\uFEFF\u00AD]/gu;
var WHITESPACE_RUN = /\s+/g;
var CHAIN_TEXT_MAX_CHARS = 64;
var CHAIN_TICKER_MAX_CHARS = 16;
function chainText(value, opts = {}) {
  const cleaned = String(value ?? "").replace(UNSAFE_CHARS, "").replace(WHITESPACE_RUN, " ").trim();
  const maxChars = opts.maxChars ?? CHAIN_TEXT_MAX_CHARS;
  if (cleaned.length <= maxChars) {
    return { text: cleaned, title: cleaned, truncated: false };
  }
  return { text: `${cleaned.slice(0, maxChars)}\u2026`, title: cleaned, truncated: true };
}
function safeChainText(value, opts = {}) {
  return chainText(value, opts).text;
}
function displayChainText(value, opts = {}) {
  const cleaned = chainText(value, { maxChars: Number.POSITIVE_INFINITY });
  const shortened = shortenIdentifiersIn(shortenLongNumbers(cleaned.text));
  return { text: chainText(shortened, opts).text, title: cleaned.title };
}
function safeTicker(value) {
  return chainText(value, { maxChars: CHAIN_TICKER_MAX_CHARS });
}

// src/display/nodeKinds.ts
var GRAPH_NODE_KINDS = [
  "eoa",
  "contract",
  "entity",
  "cluster",
  "tx",
  "exchange",
  "defi",
  "token",
  "message",
  "oracle",
  "bridge",
  "don",
  "mixer",
  "verification",
  "relayer",
  "settlement"
];
var NODE_KIND_META = {
  eoa: { label: "Wallet", authorable: true },
  contract: { label: "Contract", authorable: true },
  entity: { label: "Entity", authorable: true },
  cluster: { label: "Cluster" },
  tx: { label: "Transaction" },
  exchange: { label: "Exchange", badge: "CEX", authorable: true },
  defi: { label: "DeFi protocol", badge: "DeFi", authorable: true },
  token: { label: "Token", badge: "Token", authorable: true },
  // A cross-chain message payload (e.g. a Tokemak message riding CCIP), anchored
  // on-chain under the recipient. On-chain (so it stays inside its chain
  // rectangle) but reads as a structured protocol artifact, not a wallet.
  message: { label: "Message", badge: "Message" },
  oracle: { label: "Oracle", badge: "Oracle", offChain: true, authorable: true },
  bridge: { label: "Bridge", badge: "Bridge", offChain: true, authorable: true },
  don: { label: "Oracle network", badge: "DON", offChain: true },
  mixer: { label: "Mixer", badge: "Mixer", offChain: true, authorable: true },
  // A cross-chain verification/attestation step (CCIP's Committee Verifier +
  // Executor). Off-chain infra like the DON it hangs under.
  verification: { label: "Verification", badge: "Verified", offChain: true },
  // An off-chain message-delivery relayer / executor (Wormhole Executor,
  // LayerZero executor, Across relayer). Distinct from the bridge *hub* it
  // serves — it carries the message after attestation.
  relayer: { label: "Relayer", badge: "Relayer", offChain: true },
  // The off-chain venue that fills a posted order — a CoW solver batch auction,
  // a Fusion/UniswapX filler network. Like a DON it is a set of competing agents
  // the transaction hands work to, not a route value travels along.
  settlement: { label: "Settlement venue", badge: "Settlement", offChain: true }
};
var SCENE_KIND_TO_DOMAIN_KIND = {
  // On-chain.
  address: "eoa",
  eoa: "eoa",
  wallet: "eoa",
  contract: "contract",
  defi: "defi",
  token: "token",
  message: "message",
  tx: "tx",
  // Resolved catalog entities.
  entity: "entity",
  cluster: "cluster",
  exchange: "exchange",
  cex: "exchange",
  // Off-chain infrastructure.
  oracle: "oracle",
  bridge: "bridge",
  don: "don",
  mixer: "mixer",
  virtual: "entity",
  verification: "verification",
  attestation: "verification",
  settlement: "settlement",
  relayer: "relayer"
};
function domainKindForSceneKind(kind, opts = {}) {
  if (kind) {
    const mapped = SCENE_KIND_TO_DOMAIN_KIND[kind];
    if (mapped) return mapped;
  }
  if (opts.offChain) return "entity";
  return void 0;
}
function labelForKind(kind) {
  return kind ? NODE_KIND_META[kind].label : void 0;
}
function badgeForKind(kind) {
  return kind ? NODE_KIND_META[kind].badge : void 0;
}
function isOffChainKind(kind) {
  return kind != null && NODE_KIND_META[kind].offChain === true;
}
function nodeKindLabel(kind, opts = {}) {
  const domain = domainKindForSceneKind(kind, opts);
  if (domain) return NODE_KIND_META[domain].label;
  if (!kind) return void 0;
  const words = kind.replace(/[_-]+/g, " ").trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : kind;
}
var AUTHORABLE_NODE_KINDS = GRAPH_NODE_KINDS.filter((k) => NODE_KIND_META[k].authorable).map((kind) => ({
  kind,
  label: NODE_KIND_META[kind].label
}));

// src/display/index.ts
var TX_FIELDS = {
  canonicalTxId: "canonical_tx_id",
  txHash: "tx_hash",
  chainId: "chain_id",
  blockNumber: "block_number",
  blockHash: "block_hash",
  blockTimestamp: "block_timestamp",
  transactionIndex: "transaction_index",
  type: "type",
  finality: "finality",
  direction: "direction",
  status: "status",
  totalValueRaw: "total_value_raw",
  amountHidden: "amount_hidden",
  valueUsd: "value_usd",
  primaryEntityId: "primary_entity_id",
  // `primaryProtocolId` resolves to whatever standard the TOP-LEVEL call matched,
  // so it is frequently a token standard (`erc721`) while the protocol the row is
  // interesting for sits in `protocolIds` alongside it. Surfaces that answer
  // "which protocol is this?" should read the set, not the primary alone.
  primaryProtocolId: "primary_protocol_id",
  protocolIds: "protocol_ids",
  from: "from",
  to: "to",
  counts: "counts",
  createdAt: "created_at",
  updatedAt: "updated_at",
  // Decode glass-box, three independent questions. `decodeConfidence` grades the
  // SEMANTIC reading ("this is a bridge send"); `decodeCoverage`/`decodeCounts`
  // say how much of the tx that reading covers; `abiSource`/`abiConfidence` state
  // the MECHANICAL provenance of the top-level call's ABI. A row can be
  // semantically confident, thinly covered and mechanically weak at once, so they
  // stay separate fields rather than collapsing into one "quality" number.
  decodeConfidence: "decode_confidence",
  decodeCoverage: "decode_coverage",
  decodeCounts: "decode_counts",
  explanation: "explanation",
  abiSource: "abi_source",
  abiConfidence: "abi_confidence",
  // The mechanical half of the "why": which tenancy rung's ABI decoded the
  // top-level call. Separate from `explanation`, which explains the semantic
  // reading — a row can be read correctly by an ABI the operator did not expect
  // to win, and only these bullets name the rung.
  abiExplanation: "abi_explanation",
  // WHICH PIPELINE produced this copy. A fourth independent question, and the
  // one that frames the other three: "coverage 100%" is true of whatever
  // pipeline ran, and the cheapest tier reaches it by not looking. The server
  // decides `belowDefault` — never the most-complete tier, which would flag
  // almost every row — so no client re-derives which tier counts as thin.
  pipelineTier: "pipeline_tier",
  pipelineTierLabel: "pipeline_tier_label",
  pipelineTierBelowDefault: "pipeline_tier_below_default"
};
var FIELD_LABELS = {
  // transaction
  canonical_tx_id: "Transaction ID",
  tx_hash: "Tx Hash",
  chain_id: "Chain",
  block_number: "Block",
  block_hash: "Block Hash",
  block_timestamp: "Time",
  transaction_index: "Index",
  type: "Type",
  finality: "Finality",
  direction: "Direction",
  status: "Status",
  total_value_raw: "Value",
  amount_hidden: "Amount hidden",
  value_usd: "Value (USD)",
  // Distinct from the `entity` catalog label below, which is the RESOLVED entity
  // (a named chip from the precedence engine). This is the raw id the row was
  // stamped with at ingest, and the two can appear side by side — a column
  // menu offering "Entity" twice is a menu that can't be used.
  primary_entity_id: "Entity ID",
  primary_protocol_id: "Primary protocol",
  protocol_ids: "Protocols",
  from: "From",
  to: "To",
  counts: "Counts",
  created_at: "Created",
  updated_at: "Updated",
  decode_confidence: "Decode confidence",
  decode_coverage: "Decode coverage",
  decode_counts: "Decode coverage detail",
  abi_source: "ABI source",
  abi_confidence: "ABI confidence",
  abi_explanation: "ABI provenance",
  pipeline_tier_label: "Pipeline",
  // canonical-tx attr leaves (shared with `@authe/fe-commons/triggers`, which
  // resolves its field-path labels through `fieldLabel` so the rule builder and
  // the transaction table can never disagree on a name).
  from_address: "From",
  to_address: "To",
  value: "Value (native)",
  input_data: "Input Data",
  total_usd: "Value (USD)",
  // enrichment payload leaves (trigger DSL `<kind>.<path>`)
  score: "Risk Score",
  bucket: "Risk Bucket",
  policy_id: "Risk Policy",
  catalog_id: "Catalog ID",
  kind: "Kind",
  // address / catalog
  address: "Address",
  label: "Label",
  tag: "Tag",
  entity: "Entity",
  group: "Group",
  protocol: "Protocol",
  // A machine cross-chain saga (a bridge's legs tied by the protocol's own
  // correlation id) — labelled by what it shows rather than by the internal
  // "saga" term, which means nothing to an investigator.
  // What the transaction DID, as the decode read it — "Approve", "Mint
  // Signed", "Deposit Native 0.01 ETH". Distinct from `type`, which is the
  // classification ("transaction") and says nothing about the action.
  tx_action: "Action",
  // WHICH assets moved, as opposed to `value`'s how-much-in-native-and-USD.
  // An NFT purchase reads in `value` as the ETH that left it; the token it
  // bought only appears here.
  assets: "Assets",
  saga: "Cross-chain",
  // The correlation id itself, as opposed to `saga`'s "did every leg arrive".
  // Generic because the protocol names it: a CCIP message id, a Wormhole VAA.
  cross_chain_id: "Cross-chain ID",
  // Catalog attribution comes in two subjects that are easy to confuse and were:
  // what someone said about THIS TRANSFER, versus what they said about the WALLET
  // on the other end. Both label sets are called "labels" by their own API, so
  // the column headers are where the distinction has to be made visible.
  tx_labels: "Labels",
  tx_tags: "Tags",
  tx_groups: "Groups",
  tx_entity: "Entity",
  counterparty_labels: "Counterparty labels",
  counterparty_tags: "Counterparty tags",
  counterparty_groups: "Counterparty groups",
  counterparty_entity: "Counterparty entity",
  counterparty_risk: "Counterparty risk",
  row_flags: "Flags",
  // The SENDING address's attribution — the same claim as `counterparty_*` about
  // the row's other end. Named by role rather than by position for the same
  // reason `counterparty_*` is: a header reading "From labels" would be read as
  // "labels that came from somewhere".
  sender_labels: "Sender labels",
  sender_tags: "Sender tags",
  sender_groups: "Sender groups",
  sender_entity: "Sender entity",
  notes: "Note",
  triage: "Triage",
  risk_score: "Risk",
  // catalog rows (entities / tags / labels / groups — canonical commons fields)
  name: "Name",
  description: "Description",
  entity_type: "Type",
  risk_level: "Risk Level",
  website: "Website",
  twitter: "Twitter",
  category: "Category",
  color: "Colour",
  icon: "Icon",
  logo_s3_uri: "Logo",
  lifecycle: "Lifecycle",
  severity: "Severity",
  aliases: "Aliases",
  chain: "Chain",
  parent_entity_id: "Parent Entity",
  related_entity_ids: "Related Entities",
  member_tag_ids: "Tags",
  member_label_ids: "Labels",
  member_entity_ids: "Entities",
  system_default: "System Default",
  confidence: "Confidence",
  explanation: "Why",
  source: "Source",
  is_human_approved: "Approved",
  // tenancy
  org_id: "Organization",
  tenant_id: "Tenant",
  customer_id: "Customer",
  user_id: "User"
};
var IDENTITY_NAME_FALLBACK = ["display_name", "name", "slug"];
function entityDisplayName(source, opts = {}) {
  const fields = opts.fields ?? IDENTITY_NAME_FALLBACK;
  if (source) {
    for (const field of fields) {
      const value = source[field];
      if (typeof value === "string" && value.trim()) return value.trim();
    }
  }
  const id = opts.fallbackId;
  return typeof id === "string" && id.trim() ? id.trim() : null;
}
function titleCase(key) {
  return key.replace(/[_-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()).trim();
}
function fieldLabel(key) {
  return FIELD_LABELS[key] ?? titleCase(key);
}
function fieldLabels(keys, overrides) {
  const out = {};
  for (const key of keys) out[key] = fieldLabel(key);
  return overrides ? { ...out, ...overrides } : out;
}
var IRREGULAR_PLURALS = {
  person: "people",
  child: "children",
  alias: "aliases",
  status: "statuses"
};
function applyLeadingCase(source, target) {
  const first = source.charAt(0);
  if (first && first === first.toUpperCase() && first !== first.toLowerCase()) {
    return target.charAt(0).toUpperCase() + target.slice(1);
  }
  return target;
}
function pluralize(word, count) {
  if (count === 1) return word;
  const trimmed = word.trim();
  if (!trimmed) return word;
  const irregular = IRREGULAR_PLURALS[trimmed.toLowerCase()];
  if (irregular) return applyLeadingCase(trimmed, irregular);
  if (/[^aeiou]y$/i.test(trimmed)) return `${trimmed.slice(0, -1)}ies`;
  if (/(s|x|z|ch|sh)$/i.test(trimmed)) return `${trimmed}es`;
  return `${trimmed}s`;
}
var ARTICLE_EXCEPTIONS = {
  // Consonant sound behind a vowel letter.
  user: "a",
  url: "a",
  uuid: "a",
  unit: "a",
  union: "a",
  // Vowel sound behind a consonant letter.
  hour: "an",
  honest: "an"
};
function indefiniteArticle(noun, capitalize = false) {
  const first = (noun.trim().split(/[\s-]/)[0] ?? "").toLowerCase();
  const article = ARTICLE_EXCEPTIONS[first] ?? (/^[aeiou]/.test(first) ? "an" : "a");
  return capitalize ? `${article[0].toUpperCase()}${article.slice(1)}` : article;
}
function withArticle(noun, capitalize = false) {
  return `${indefiniteArticle(noun, capitalize)} ${noun}`;
}
function pluralizeLabel(key, count) {
  return pluralize(fieldLabel(key), count);
}
function countLabel(count, word, display) {
  return `${display ?? count} ${pluralize(word, count)}`;
}
var GRAPH_VIEW_MODES = ["hierarchical", "temporal", "force"];
var GRAPH_VIEW_MODE_LABELS = {
  hierarchical: "Hierarchical",
  temporal: "Timeline",
  force: "Force"
};
var GRAPH_FLOW_VIEW_MODES = GRAPH_VIEW_MODES.filter(
  (mode) => mode !== "force"
);
function resolveGraphViewMode(stored, offered = GRAPH_VIEW_MODES) {
  if (stored && offered.includes(stored)) return stored;
  return offered[0] ?? "hierarchical";
}
var GRAPH_DETAIL_TIERS = SCENE_DETAIL_TIERS;
var GRAPH_DETAIL_TIER_LABELS = {
  compact: "Compact",
  standard: "Standard",
  expanded: "Expanded"
};
var DEFAULT_GRAPH_DETAIL_TIER = "compact";
var GRAPH_PANEL_KEYS = ["legend", "highlights", "stats", "minimap"];
var GRAPH_PANEL_LABELS = {
  legend: "Legend",
  highlights: "Transaction highlights",
  stats: "Chain stats",
  minimap: "Minimap"
};
var ACTIVITY_VIEWS = ["table", "graph"];
var ACTIVITY_VIEW_LABELS = {
  table: "Table",
  graph: "Graph"
};
var LIVE_MODES = ["live", "interval", "off"];
var LIVE_MODE_LABELS = {
  live: "Live",
  interval: "Auto-refresh",
  off: "Off"
};
var DEFAULT_LIVE_REFRESH_MS = 15e3;
function liveModesFor(capabilities) {
  const modes = [];
  if (capabilities.serverStreaming) modes.push("live");
  if (capabilities.autoRefresh !== false) modes.push("interval");
  modes.push("off");
  return modes;
}
function allowedLiveMode(mode, modes) {
  if (modes.includes(mode)) return mode;
  const fallback = LIVE_MODES.slice(LIVE_MODES.indexOf(mode) + 1).find((m) => modes.includes(m));
  return fallback ?? modes[modes.length - 1] ?? "off";
}
var TABLE_DENSITIES = ["comfortable", "normal", "compact"];
var DEFAULT_TABLE_DENSITY = "comfortable";
var TABLE_DENSITY_LABELS = {
  comfortable: "Comfortable",
  normal: "Normal",
  compact: "Compact"
};
var TABLE_DENSITY_ROW_HEIGHT = {
  comfortable: 44,
  normal: 38,
  compact: 32
};
function resolveDensity(input) {
  return input.override ?? input.stored ?? input.fallback ?? DEFAULT_TABLE_DENSITY;
}
function sentenceList(items, type = "conjunction") {
  const parts = items.filter(Boolean);
  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0];
  const ListFormat = Intl.ListFormat;
  if (ListFormat) return new ListFormat(void 0, { style: "long", type }).format(parts);
  const word = type === "disjunction" ? "or" : "and";
  return `${parts.slice(0, -1).join(", ")} ${word} ${parts[parts.length - 1]}`;
}
function shortId(id) {
  if (id.length <= 18) return id;
  return truncateMiddle(id, { head: 10, tail: 4 });
}
function idTail(targetId) {
  if (!targetId) return "";
  const last = targetId.split("/").filter(Boolean).pop() ?? targetId;
  return last.length > 12 ? `${ELLIPSIS}${last.slice(-8)}` : last;
}
function initials(source, max = 2) {
  const parts = (source ?? "").trim().split(/[\s._-]+/).map((part) => part.replace(/^[^\p{L}\p{N}]+/u, "")).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, max).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
var DISPLAY_PREF_KEYS = {
  tableDensity: "display:table-density",
  columnVisibility: "display:columns",
  regionAliasPin: "display:region-alias-pin",
  graphViewMode: "display:graph-view-mode",
  graphDetailTier: "display:graph-detail-tier",
  graphHiddenPanels: "display:graph-panels",
  graphGrouping: "display:graph-grouping",
  activityView: "display:activity-view",
  liveMode: "display:live-mode",
  inspector: "display:inspector",
  attributionChips: "display:attribution-chips"
};
var ATTRIBUTION_KINDS = ["entity", "label", "tag", "group"];
var DEFAULT_ATTRIBUTION_CHIP_PREFS = {
  kinds: ATTRIBUTION_KINDS,
  max: 3,
  logos: true,
  size: "sm",
  showProvenance: false,
  showWhy: true
};
function resolveAttributionChipPrefs(override, userPref) {
  return {
    ...DEFAULT_ATTRIBUTION_CHIP_PREFS,
    ...stripUndefined(userPref),
    ...stripUndefined(override)
  };
}
function stripUndefined(prefs) {
  if (!prefs) return {};
  return Object.fromEntries(
    Object.entries(prefs).filter(([, value]) => value !== void 0)
  );
}
var DEFAULT_REGION_ALIAS_PIN = "default";
function coerceEnum(value, allowed, fallback) {
  return typeof value === "string" && allowed.includes(value) ? value : fallback;
}
var DisplayPrefsStore = class _DisplayPrefsStore {
  constructor(storage) {
    this.storage = storage;
  }
  storage;
  static {
    brandClass(this, "display/DisplayPrefsStore");
  }
  /** Derive a store for a different identity (e.g. on tenant switch). */
  with(patch) {
    return new _DisplayPrefsStore(this.storage.with(patch));
  }
  getTableDensity(fallback = "comfortable") {
    return coerceEnum(
      this.storage.get(DISPLAY_PREF_KEYS.tableDensity, fallback),
      TABLE_DENSITIES,
      fallback
    );
  }
  setTableDensity(density, level) {
    return this.storage.set(DISPLAY_PREF_KEYS.tableDensity, density, level);
  }
  getGraphViewMode(fallback = "hierarchical") {
    return coerceEnum(
      this.storage.get(DISPLAY_PREF_KEYS.graphViewMode, fallback),
      GRAPH_VIEW_MODES,
      fallback
    );
  }
  setGraphViewMode(mode, level) {
    return this.storage.set(DISPLAY_PREF_KEYS.graphViewMode, mode, level);
  }
  getGraphDetailTier(fallback = DEFAULT_GRAPH_DETAIL_TIER) {
    return coerceEnum(
      this.storage.get(DISPLAY_PREF_KEYS.graphDetailTier, fallback),
      GRAPH_DETAIL_TIERS,
      fallback
    );
  }
  setGraphDetailTier(tier, level) {
    return this.storage.set(DISPLAY_PREF_KEYS.graphDetailTier, tier, level);
  }
  getActivityView(fallback = "table") {
    return coerceEnum(
      this.storage.get(DISPLAY_PREF_KEYS.activityView, fallback),
      ACTIVITY_VIEWS,
      fallback
    );
  }
  setActivityView(view, level) {
    return this.storage.set(DISPLAY_PREF_KEYS.activityView, view, level);
  }
  getLiveMode(fallback = "interval") {
    return coerceEnum(this.storage.get(DISPLAY_PREF_KEYS.liveMode, fallback), LIVE_MODES, fallback);
  }
  setLiveMode(mode, level) {
    return this.storage.set(DISPLAY_PREF_KEYS.liveMode, mode, level);
  }
  /** See {@link DisplayPrefs.inspector}. Anything non-boolean reads as the default. */
  getInspector(fallback = false) {
    const value = this.storage.get(DISPLAY_PREF_KEYS.inspector, fallback);
    return typeof value === "boolean" ? value : fallback;
  }
  setInspector(enabled, level) {
    return this.storage.set(DISPLAY_PREF_KEYS.inspector, enabled, level);
  }
  /**
   * See {@link DisplayPrefs.attributionChips}. Returns only the fields the user
   * has actually expressed, so the caller can layer it under a surface override
   * without a stored default silently outranking one.
   */
  getAttributionChips() {
    const raw = this.storage.get(DISPLAY_PREF_KEYS.attributionChips, {});
    if (!raw || typeof raw !== "object") return {};
    const stored = raw;
    const out = {};
    if (Array.isArray(stored.kinds)) {
      const kinds = stored.kinds.filter(
        (k) => ATTRIBUTION_KINDS.includes(k)
      );
      if (kinds.length > 0) out.kinds = kinds;
    }
    if (typeof stored.max === "number" && Number.isFinite(stored.max) && stored.max >= 0) {
      out.max = Math.floor(stored.max);
    }
    if (typeof stored.logos === "boolean") out.logos = stored.logos;
    if (stored.size === "sm" || stored.size === "md") out.size = stored.size;
    if (typeof stored.showProvenance === "boolean") out.showProvenance = stored.showProvenance;
    return out;
  }
  setAttributionChips(prefs, level) {
    return this.storage.set(DISPLAY_PREF_KEYS.attributionChips, prefs, level);
  }
  getRegionAliasPin(fallback = DEFAULT_REGION_ALIAS_PIN) {
    const value = this.storage.get(DISPLAY_PREF_KEYS.regionAliasPin, fallback);
    return typeof value === "string" ? value : fallback;
  }
  setRegionAliasPin(alias, level) {
    return this.storage.set(DISPLAY_PREF_KEYS.regionAliasPin, alias, level);
  }
  /**
   * Overlay panels the user dismissed on one graph surface.
   *
   * Keyed per graph (as column visibility is keyed per table) because dismissing
   * the legend on a dense case graph shouldn't hide it on a two-node transaction
   * graph. Stores the DISMISSED set, not the visible one, so a panel added later
   * shows up by default instead of being retroactively hidden by an older pref.
   * Unknown keys are dropped on read.
   */
  getGraphHiddenPanels(graphId, fallback = []) {
    const stored = this.storage.get(
      `${DISPLAY_PREF_KEYS.graphHiddenPanels}:${graphId}`,
      fallback
    );
    if (!Array.isArray(stored)) return fallback;
    return stored.filter(
      (key) => GRAPH_PANEL_KEYS.includes(key)
    );
  }
  setGraphHiddenPanels(graphId, hidden, level) {
    return this.storage.set(`${DISPLAY_PREF_KEYS.graphHiddenPanels}:${graphId}`, [...hidden], level);
  }
  /**
   * How the user chose to band one graph surface (the "Group by" order).
   *
   * Keyed per graph for the same reason the panels are: grouping a case file by
   * entity says nothing about how a single transaction should be drawn. Parsed
   * on read, so a dimension this build no longer knows is dropped rather than
   * breaking the graph.
   */
  getGraphGrouping(graphId, fallback = null) {
    const stored = this.storage.get(
      `${DISPLAY_PREF_KEYS.graphGrouping}:${graphId}`,
      void 0
    );
    return parseGroupingSpec(stored) ?? fallback;
  }
  setGraphGrouping(graphId, grouping, level) {
    return this.storage.set(`${DISPLAY_PREF_KEYS.graphGrouping}:${graphId}`, grouping, level);
  }
  /** Column visibility for one table id (`{ [columnId]: visible }`). */
  getColumnVisibility(tableId, fallback = {}) {
    return this.storage.get(
      `${DISPLAY_PREF_KEYS.columnVisibility}:${tableId}`,
      fallback
    );
  }
  setColumnVisibility(tableId, visibility, level) {
    return this.storage.set(`${DISPLAY_PREF_KEYS.columnVisibility}:${tableId}`, visibility, level);
  }
  /** Load the non-table-scoped prefs as one bag (column visibility is per-table). */
  load() {
    return {
      tableDensity: this.getTableDensity(),
      regionAliasPin: this.getRegionAliasPin(),
      graphViewMode: this.getGraphViewMode(),
      graphDetailTier: this.getGraphDetailTier(),
      activityView: this.getActivityView(),
      liveMode: this.getLiveMode(),
      inspector: this.getInspector(),
      attributionChips: this.getAttributionChips()
    };
  }
  /**
   * Persist whichever prefs are present (skips `undefined` fields).
   *
   * `true` only when every field it actually wrote persisted — same contract as
   * the individual setters, so a bulk save cannot claim more than they do.
   * Every field is attempted regardless: a full store is a property of the
   * store, not of one key, but stopping early would make the result depend on
   * field order.
   */
  save(prefs, level) {
    let stored = true;
    if (prefs.tableDensity) stored = this.setTableDensity(prefs.tableDensity, level) && stored;
    if (prefs.regionAliasPin != null) {
      stored = this.setRegionAliasPin(prefs.regionAliasPin, level) && stored;
    }
    if (prefs.graphViewMode) stored = this.setGraphViewMode(prefs.graphViewMode, level) && stored;
    if (prefs.graphDetailTier) {
      stored = this.setGraphDetailTier(prefs.graphDetailTier, level) && stored;
    }
    if (prefs.activityView) stored = this.setActivityView(prefs.activityView, level) && stored;
    if (prefs.liveMode) stored = this.setLiveMode(prefs.liveMode, level) && stored;
    if (prefs.inspector != null) stored = this.setInspector(prefs.inspector, level) && stored;
    if (prefs.attributionChips) {
      stored = this.setAttributionChips(prefs.attributionChips, level) && stored;
    }
    if (prefs.columnVisibility) {
      for (const [tableId, visibility] of Object.entries(prefs.columnVisibility)) {
        stored = this.setColumnVisibility(tableId, visibility, level) && stored;
      }
    }
    if (prefs.graphHiddenPanels) {
      for (const [graphId, hidden] of Object.entries(prefs.graphHiddenPanels)) {
        stored = this.setGraphHiddenPanels(graphId, hidden, level) && stored;
      }
    }
    if (prefs.graphGrouping) {
      for (const [graphId, grouping] of Object.entries(prefs.graphGrouping)) {
        stored = this.setGraphGrouping(graphId, grouping, level) && stored;
      }
    }
    return stored;
  }
};
function createDisplayPrefsStore(storage) {
  return new DisplayPrefsStore(storage);
}

export { ACTIVITY_VIEWS, ACTIVITY_VIEW_LABELS, ATTRIBUTION_KINDS, AUTHORABLE_NODE_KINDS, CHAIN_TEXT_MAX_CHARS, CHAIN_TICKER_MAX_CHARS, DEFAULT_ATTRIBUTION_CHIP_PREFS, DEFAULT_GRAPH_DETAIL_TIER, DEFAULT_LIVE_REFRESH_MS, DEFAULT_REGION_ALIAS_PIN, DEFAULT_TABLE_DENSITY, DEFAULT_TRUNCATION, DISPLAY_PREF_KEYS, DisplayPrefsStore, ELLIPSIS, FIELD_LABELS, GRAPH_DETAIL_TIERS, GRAPH_DETAIL_TIER_LABELS, GRAPH_FLOW_VIEW_MODES, GRAPH_NODE_KINDS, GRAPH_PANEL_KEYS, GRAPH_PANEL_LABELS, GRAPH_VIEW_MODES, GRAPH_VIEW_MODE_LABELS, IDENTITY_NAME_FALLBACK, LIVE_MODES, LIVE_MODE_LABELS, NODE_KIND_META, TABLE_DENSITIES, TABLE_DENSITY_LABELS, TABLE_DENSITY_ROW_HEIGHT, TX_FIELDS, allowedLiveMode, asString, asTrimmedString, badgeForKind, chainText, configureTruncation, countLabel, createDisplayPrefsStore, displayChainText, domainKindForSceneKind, entityDisplayName, fieldLabel, fieldLabels, getTruncation, hexPrefixOf, idTail, indefiniteArticle, initials, isOffChainKind, labelForKind, liveModesFor, nodeKindLabel, num, pluralize, pluralizeLabel, resetTruncationConfig, resolveAttributionChipPrefs, resolveDensity, resolveGraphViewMode, safeChainText, safeTicker, sentenceList, shortId, shortenIdentifiersIn, str, text, truncateMiddle, withArticle };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map